import {
  useMatchRoute,
  useRouterState,
  type RegisteredRouter,
  type UseMatchRouteOptions,
} from '@tanstack/react-router';

/**
 * Whether the current location matches `opts`, reactively.
 *
 * Calling `useMatchRoute()`'s result directly in render freezes the value: the
 * callback identity is stable, so the React Compiler memoizes the call once and
 * never recomputes it (TanStack/router#4499). Evaluating the match inside a
 * `useRouterState` selector instead means the value arrives from a hook, and the
 * compiler never memoizes hook calls.
 *
 * Workaround, not architecture — delete this hook together with the
 * `route-active/no-render-time-match-route` lint rule once upstream lands the
 * fix. Removal criteria and links are documented on that rule:
 * packages/config/oxlint-plugins/src/no-render-time-match-route.ts
 */
export function useIsRouteActive<const TTo extends string | undefined>(
  opts: UseMatchRouteOptions<RegisteredRouter, string, TTo>,
) {
  const matchRoute = useMatchRoute();
  return useRouterState({
    select: () => opts.to !== undefined && !!matchRoute(opts),
  });
}
